import type { Vehicle } from '@agos/api-client/model'
import { T, tt } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
} from '@agos/ui'
import { Loader2, Save, X } from 'lucide-react'
import { useState, type FormEvent, type ReactNode } from 'react'

/**
 * Adding or editing a car.
 *
 * Numbers go out as numbers and dates as `YYYY-MM-DD` — the shape legacy sends,
 * and the shape the API's `JsonNode` body accepts without complaint. An empty
 * date is sent as `null` rather than omitted, because clearing an expiry is a
 * thing a manager does when a certificate is replaced.
 */

const FIELD_CLASSES =
  'w-full px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

const FUEL_TYPES = [
  { value: 'benzin', label: tt('Benzín') },
  { value: 'nafta', label: tt('Nafta') },
  { value: 'lpg_benzin', label: tt('LPG/Benzín') },
] as const

const TIRE_TYPES = [
  { value: 'summer', label: tt('Letné') },
  { value: 'winter', label: tt('Zimné') },
] as const

/** `2026-07-29T00:00:00Z` → `2026-07-29`, which is what a date input wants. */
function dateInputValue(value: string | undefined): string {
  return value ? value.slice(0, 10) : ''
}

export function VehicleModal({
  vehicle,
  onClose,
  onSave,
}: {
  vehicle: Vehicle | null
  onClose: () => void
  onSave: (form: Record<string, unknown>) => Promise<void>
}) {
  const [name, setName] = useState(vehicle?.name ?? '')
  const [licensePlate, setLicensePlate] = useState(vehicle?.licensePlate ?? '')
  const [odometer, setOdometer] = useState(String(vehicle?.odometer ?? 0))
  const [fuelConsumption, setFuelConsumption] = useState(String(vehicle?.fuelConsumption ?? ''))
  const [fuelType, setFuelType] = useState(vehicle?.fuelType ?? 'benzin')
  const [fuelPrice, setFuelPrice] = useState(String(vehicle?.fuelPrice ?? ''))
  const [stkExpiry, setStkExpiry] = useState(dateInputValue(vehicle?.stkExpiry))
  const [ekExpiry, setEkExpiry] = useState(dateInputValue(vehicle?.ekExpiry))
  const [insuranceExpiry, setInsuranceExpiry] = useState(dateInputValue(vehicle?.insuranceExpiry))
  const [vignetteExpiry, setVignetteExpiry] = useState(dateInputValue(vehicle?.vignetteExpiry))
  const [tireType, setTireType] = useState(vehicle?.tireType ?? 'summer')
  const [active, setActive] = useState(vehicle?.active !== false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim() || !licensePlate.trim()) {
      setError(tt('Názov a značka sú povinné'))
      return
    }

    setIsSaving(true)
    setError('')
    try {
      await onSave({
        name: name.trim(),
        licensePlate: licensePlate.trim(),
        odometer: Number(odometer) || 0,
        fuelConsumption: Number(fuelConsumption) || 0,
        fuelType,
        fuelPrice: Number(fuelPrice) || 0,
        // Null, not omitted: a replaced certificate has its old date cleared.
        stkExpiry: stkExpiry || null,
        ekExpiry: ekExpiry || null,
        insuranceExpiry: insuranceExpiry || null,
        vignetteExpiry: vignetteExpiry || null,
        tireType,
        active,
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <h2 className="font-bold">
            {vehicle ? <T>Upraviť vozidlo</T> : <T>Pridať vozidlo</T>}
          </h2>
          <button
            type="button"
            aria-label={tt('Zatvoriť')}
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={(event) => void submit(event)} className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={tt('Názov')}>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                aria-label={tt('Názov')}
                className={FIELD_CLASSES}
              />
            </Field>
            <Field label={tt('Značka')}>
              <input
                value={licensePlate}
                onChange={(event) => setLicensePlate(event.target.value.toUpperCase())}
                aria-label={tt('Značka')}
                className={cn(FIELD_CLASSES, 'font-mono uppercase')}
              />
            </Field>
            <Field label={tt('Začiatočný stav km')}>
              <input
                type="number"
                min="0"
                value={odometer}
                onChange={(event) => setOdometer(event.target.value)}
                aria-label={tt('Začiatočný stav km')}
                className={cn(FIELD_CLASSES, 'tabular-nums')}
              />
            </Field>
            <Field label="l/100km">
              <input
                type="number"
                min="0"
                step="0.1"
                value={fuelConsumption}
                onChange={(event) => setFuelConsumption(event.target.value)}
                aria-label="l/100km"
                className={cn(FIELD_CLASSES, 'tabular-nums')}
              />
            </Field>
            <Field label={tt('Palivo')}>
              <select
                value={fuelType}
                onChange={(event) => setFuelType(event.target.value)}
                aria-label={tt('Palivo')}
                className={FIELD_CLASSES}
              >
                {FUEL_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={tt('Cena za liter')}>
              <input
                type="number"
                min="0"
                step="0.001"
                value={fuelPrice}
                onChange={(event) => setFuelPrice(event.target.value)}
                aria-label={tt('Cena za liter')}
                className={cn(FIELD_CLASSES, 'tabular-nums')}
              />
            </Field>
            <Field label="STK">
              <input
                type="date"
                value={stkExpiry}
                onChange={(event) => setStkExpiry(event.target.value)}
                aria-label="STK"
                className={FIELD_CLASSES}
              />
            </Field>
            <Field label="EK">
              <input
                type="date"
                value={ekExpiry}
                onChange={(event) => setEkExpiry(event.target.value)}
                aria-label="EK"
                className={FIELD_CLASSES}
              />
            </Field>
            <Field label={tt('Poistka')}>
              <input
                type="date"
                value={insuranceExpiry}
                onChange={(event) => setInsuranceExpiry(event.target.value)}
                aria-label={tt('Poistka')}
                className={FIELD_CLASSES}
              />
            </Field>
            <Field label={tt('Diaľničná známka')}>
              <input
                type="date"
                value={vignetteExpiry}
                onChange={(event) => setVignetteExpiry(event.target.value)}
                aria-label={tt('Diaľničná známka')}
                className={FIELD_CLASSES}
              />
            </Field>
            <Field label={tt('Pneumatiky')}>
              <select
                value={tireType}
                onChange={(event) => setTireType(event.target.value)}
                aria-label={tt('Pneumatiky')}
                className={FIELD_CLASSES}
              >
                {TIRE_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="px-6 pb-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={active}
                onChange={(event) => setActive(event.target.checked)}
                className="w-4 h-4 rounded border-border"
              />
              <T>Vozidlo je v prevádzke</T>
            </label>
          </div>

          {error && <p className="px-6 pb-3 text-sm text-red-500">{error}</p>}

          <div className="flex gap-3 px-6 py-4 border-t border-border flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className={cn(BUTTON_SECONDARY, 'flex-1 py-2.5 rounded-xl text-sm')}
            >
              <T>Zrušiť</T>
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className={cn('flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <T>Uložiť</T>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
        {label}
      </span>
      {children}
    </label>
  )
}
